/**
 * Copy of React Admin ReferenceArrayInput.
 * It adds support for a more complete pagination, sorting and filtering features.
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import PropTypes from 'prop-types';
import { useInput, useTranslate } from 'ra-core';
import { LinearProgress, Labeled } from 'react-admin';
import { TextField } from '@material-ui/core';
import { useReferenceArrayInputController } from './useReferenceArrayInputController';
/**
 * An Input component for fields containing a list of references to another resource.
 * Useful for 'hasMany' relationship.
 *
 * @example
 * The post object has many tags, so the post resource looks like:
 * {
 *    id: 1234,
 *    tag_ids: [ "1", "23", "4" ]
 * }
 *
 * ReferenceArrayInput component fetches the current resources (using
 * `dataProvider.getMany()`) as well as possible resources (using
 * `dataProvider.getMatching()`) in the reference endpoint. It then
 * delegates rendering to a subcomponent, to which it passes the possible
 * choices as the `choices` attribute.
 *
 * Use it with a selector component as child, like `<SelectArrayInput>`
 * or <CheckboxGroupInput>.
 *
 * @example
 * export const PostEdit = (props) => (
 *     <Edit {...props}>
 *         <SimpleForm>
 *             <ReferenceArrayInput source="tag_ids" reference="tags">
 *                 <SelectArrayInput optionText="name" />
 *             </ReferenceArrayInput>
 *         </SimpleForm>
 *     </Edit>
 * );
 *
 * By default, restricts the possible values to 25. You can extend this limit
 * by setting the `perPage` prop.
 *
 * @example
 * <ReferenceArrayInput
 *      source="tag_ids"
 *      reference="tags"
 *      perPage={100}>
 *     <SelectArrayInput optionText="name" />
 * </ReferenceArrayInput>
 *
 * By default, orders the possible values by id desc. You can change this order
 * by setting the `sort` prop (an object with `field` and `order` properties).
 *
 * @example
 * <ReferenceArrayInput
 *      source="tag_ids"
 *      reference="tags"
 *      sort={{ field: 'name', order: 'ASC' }}>
 *     <SelectArrayInput optionText="name" />
 * </ReferenceArrayInput>
 *
 * Also, you can filter the query used to populate the possible values. Use the
 * `filter` prop for that.
 *
 * @example
 * <ReferenceArrayInput
 *      source="tag_ids"
 *      reference="tags"
 *      filter={{ is_public: true }}>
 *     <SelectArrayInput optionText="name" />
 * </ReferenceArrayInput>
 *
 * The enclosed component may filter results. ReferenceArrayInput passes a
 * `setFilter` function as prop to its child component. It uses the value to
 * create a filter for the query - by default { q: [searchText] }. You can
 * customize the mapping searchText => searchQuery by setting a custom
 * `filterToQuery` function prop:
 *
 * @example
 * <ReferenceArrayInput
 *      source="tag_ids"
 *      reference="tags"
 *      filterToQuery={searchText => ({ name: searchText })}>
 *     <SelectArrayInput optionText="name" />
 * </ReferenceArrayInput>
 */
export const ReferenceArrayInput = (_a) => {
    var { children, id: idOverride, onBlur, onChange, onFocus, validate, parse, format } = _a, props = __rest(_a, ["children", "id", "onBlur", "onChange", "onFocus", "validate", "parse", "format"]);
    if (React.Children.count(children) !== 1) {
        throw new Error('<ReferenceArrayInput> only accepts a single child (like <Datagrid>)');
    }
    const { id, input, isRequired, meta } = useInput(Object.assign({ id: idOverride, onBlur,
        onChange,
        onFocus, source: props.source, validate,
        parse,
        format }, props));
    const controllerProps = useReferenceArrayInputController(Object.assign(Object.assign({}, props), { input }));
    const translate = useTranslate();
    return (React.createElement(ReferenceArrayInputView, Object.assign({ id: id, input: input, isRequired: isRequired, meta: meta, translate: translate, children: children }, props, controllerProps)));
};
ReferenceArrayInput.propTypes = {
    allowEmpty: PropTypes.bool,
    basePath: PropTypes.string,
    children: PropTypes.element.isRequired,
    className: PropTypes.string,
    filter: PropTypes.object,
    label: PropTypes.string,
    perPage: PropTypes.number,
    reference: PropTypes.string.isRequired,
    resource: PropTypes.string,
    sort: PropTypes.shape({
        field: PropTypes.string,
        order: PropTypes.oneOf(['ASC', 'DESC']),
    }),
    source: PropTypes.string,
};
ReferenceArrayInput.defaultProps = {
    filter: {},
    perPage: 25,
    sort: { field: 'id', order: 'DESC' },
};
const sanitizeRestProps = (_a) => {
    var { crudGetMany, crudGetMatching, perPage, referenceSource } = _a, rest = __rest(_a, ["crudGetMany", "crudGetMatching", "perPage", "referenceSource"]);
    return rest;
};
export const ReferenceArrayInputView = (_a) => {
    var { allowEmpty, availableChoices, basePath, children, choices, className, error, filter, input, loading, isRequired, label, meta, onChange, options, page, perPage, resource, selectedChoices, setFilter, setPage, setPagination, setPerPage, setSort, setSortField, setSortOrder, sort, source, total, translate, warning } = _a, rest = __rest(_a, ["allowEmpty", "availableChoices", "basePath", "children", "choices", "className", "error", "filter", "input", "loading", "isRequired", "label", "meta", "onChange", "options", "page", "perPage", "resource", "selectedChoices", "setFilter", "setPage", "setPagination", "setPerPage", "setSort", "setSortField", "setSortOrder", "sort", "source", "total", "translate", "warning"]);
    const translatedLabel = translate(label || `resources.${resource}.fields.${source}`, { _: label });
    if (loading) {
        return (React.createElement(Labeled, { label: translatedLabel, source: source, resource: resource, className: className, isRequired: isRequired },
            React.createElement(LinearProgress, null)));
    }
    if (error) {
        return (React.createElement(TextField, { error: true, disabled: true, label: label, value: error, margin: "normal" }));
    }
    return React.cloneElement(children, Object.assign(Object.assign({ allowEmpty,
        availableChoices,
        basePath,
        choices,
        className,
        error,
        filter,
        input,
        isRequired, label: translatedLabel, meta: Object.assign(Object.assign({}, meta), { helperText: warning || false }), onChange,
        options,
        page,
        perPage,
        resource,
        selectedChoices,
        setFilter,
        setPage,
        setPagination,
        setPerPage,
        setSort,
        setSortOrder,
        setSortField,
        sort,
        source,
        total, translateChoice: false, limitChoicesToValue: true }, sanitizeRestProps(rest)), children.props));
};
ReferenceArrayInputView.propTypes = {
    allowEmpty: PropTypes.bool,
    basePath: PropTypes.string,
    children: PropTypes.element,
    choices: PropTypes.array,
    className: PropTypes.string,
    error: PropTypes.string,
    loading: PropTypes.bool,
    input: PropTypes.object.isRequired,
    label: PropTypes.string,
    meta: PropTypes.object,
    onChange: PropTypes.func,
    options: PropTypes.object,
    resource: PropTypes.string.isRequired,
    setFilter: PropTypes.func,
    setPagination: PropTypes.func,
    setSort: PropTypes.func,
    source: PropTypes.string,
    translate: PropTypes.func.isRequired,
    warning: PropTypes.string,
};
//# sourceMappingURL=ReferenceArrayInput.js.map